'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { useState } from 'react';
import { Button } from '../../../../components/button';
import { Collapsible } from '../../../../components/collapsible';
import { Text } from '../../../../components/styled/text';
import { type TracePhase, type TraceToolCall } from '../types';
import { isSkillFilePath, toTraceReadFileEntry } from '../utils/read-file-tool';
import {
  getTraceTextShimmerStyle,
  shouldShowTraceToolShimmer,
} from '../utils/trace-text-shimmer';
import { TraceExpandChevron } from './TraceExpandChevron';
import { TraceReadFileContent } from './TraceReadFileContent';

type TraceReadToolCardProps = {
  toolCall: TraceToolCall;
  tracePhase?: TracePhase;
  onHideMessage?: () => void;
};

export function TraceReadToolCard({
  toolCall,
  tracePhase = 'attack',
  onHideMessage,
}: TraceReadToolCardProps) {
  const isCoPilotTrace = tracePhase === 'co-pilot';
  const [isOpen, setIsOpen] = useState(isCoPilotTrace);
  const entry = toTraceReadFileEntry(toolCall, { tracePhase });
  const headerLabel = isCoPilotTrace
    ? isSkillFilePath(entry.filePath)
      ? 'Read skill'
      : entry.displayName
    : `Read ${entry.displayName}`;
  const hasContent = entry.output.length > 0 || Boolean(entry.error?.trim());
  const hasResult = hasContent;
  const showShimmer = shouldShowTraceToolShimmer({
    hasResult,
    status: toolCall.status,
  });
  const shimmerStyle = getTraceTextShimmerStyle(showShimmer);

  if (!hasResult) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        <Text
          fontSize="xs"
          color="fg.muted"
          animationStyle={showShimmer ? 'shimmer' : undefined}
          style={shimmerStyle}
        >
          {headerLabel}
        </Text>
      </HStack>
    );
  }

  if (isCoPilotTrace) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        <TraceExpandChevron
          isExpanded={false}
          tracePhase={tracePhase}
          onHideMessage={onHideMessage}
        />
        <Text
          fontSize="xs"
          color="fg.muted"
          animationStyle={showShimmer ? 'shimmer' : undefined}
          style={shimmerStyle}
        >
          {headerLabel}
        </Text>
      </HStack>
    );
  }

  return (
    <Box bg="bg.muted" borderRadius="md" px={2} py={2} width="100%">
      <Collapsible.Root
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
        lazyMount
        unmountOnExit
      >
        <Collapsible.Trigger asChild>
          <Button
            variant="ghost"
            size="xs"
            width="full"
            justifyContent="flex-start"
          >
            <HStack gap={1} alignItems="center" minW={0}>
              <Box display="flex" alignItems="center" color="fg.muted">
                {isOpen ? (
                  <ArrowDown01Icon size={14} />
                ) : (
                  <ArrowRight01Icon size={14} />
                )}
              </Box>
              <Text
                fontSize="xs"
                fontWeight="normal"
                color="fg.muted"
                truncate
                minW={0}
              >
                {headerLabel}
              </Text>
            </HStack>
          </Button>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <Box pt={1} width="100%">
            <TraceReadFileContent
              filePath={entry.filePath}
              output={entry.output}
              error={entry.error}
              isMarkdown={entry.isMarkdown}
            />
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
